import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. You can sign in now.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center" data-testid="reset-logo">
          <div className="leading-none">
            <div className="flex items-baseline gap-0.5">
              <span className="font-heading text-2xl font-black text-[#212121] tracking-tight">DFTB</span>
              <span className="font-heading text-2xl font-black text-[#28BBE3] tracking-tight">26</span>
            </div>
            <div className="text-center text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-1">Virtual</div>
          </div>
        </Link>

        <div className="rounded-3xl bg-white ring-1 ring-black/5 p-8 sm:p-10 shadow-xl shadow-slate-200/40">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Set a new password</div>
          <h1 className="mt-2 font-heading text-3xl tracking-tighter font-medium text-slate-900">
            Choose a new password.
          </h1>

          {!token ? (
            <p className="mt-6 text-sm text-rose-600" data-testid="reset-missing-token">
              Missing or invalid reset link. Please request a new one from{" "}
              <Link to="/forgot-password" className="text-[#28BBE3] hover:underline font-semibold">
                Forgot password
              </Link>.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <Label htmlFor="reset-password" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">New password</Label>
                <Input id="reset-password" type="password" required minLength={6} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 rounded-2xl border-slate-200 bg-white py-6"
                  placeholder="At least 6 characters" data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="reset-confirm" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Confirm new password</Label>
                <Input id="reset-confirm" type="password" required minLength={6} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 rounded-2xl border-slate-200 bg-white py-6"
                  placeholder="Re-enter the password" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white py-6 font-semibold"
                data-testid="reset-submit-btn">
                {loading ? "Updating…" : "Update password"}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
